import { brandPurple } from '@src/cnst/colors.cnst'
import { mixpanelService } from '@src/srv/mixpanel.service'
import { DatoCmsFileField } from '@src/typings/dato-cms-assets'
import classNames from 'classnames'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { NcImage } from '../nc-image/NcImage.component'
import { NumberImage } from '../number-image/NumberImage.component'
import styles from './HowItWorksNc.module.scss'

export enum PageType {
  ReferAFriend = 'ReferAFriend',
  HomePage = 'HomePage',
}

export interface HowItWorksNcProps {
  sectionHeading: string
  steps: {
    id: string
    subheading: string
    paragraph: string
    image: DatoCmsFileField
  }[]
  pageType?: PageType
}

export function HowItWorksNc({
  sectionHeading,
  steps,
  pageType = PageType.HomePage,
}: HowItWorksNcProps): ReactNode {
  const [resetIntervalFlag, setResetIntervalFlag] = useState(false)
  const [stepState, setStepState] = useState({
    selectedStep: 0,
    previousStep: steps.length - 1,
  })
  // This allows for individual height calculations for each step's paragraph.
  const paragraphRefs = useRef<(HTMLParagraphElement | null)[]>([])

  const handleStepClick = useCallback(
    (index: number) => {
      setStepState(() => ({
        selectedStep: index,
        previousStep: index !== 0 ? index - 1 : steps.length - 1,
      }))
      mixpanelService.trackClick('how-it-works-item-click')
      setResetIntervalFlag(flag => !flag)
    },

    [steps.length],
  )

  useEffect(() => {
    const interval = setInterval(() => {
      setStepState(prevState => {
        const nextStep = (prevState.selectedStep + 1) % steps.length // This ensures wrapping
        return {
          selectedStep: nextStep,
          previousStep: prevState.selectedStep,
        }
      })
    }, 5000)

    return () => clearInterval(interval)
  }, [steps.length, resetIntervalFlag])

  // to calculate and return the height for each paragraph dynamically.
  function getParagraphHeight(index: number): number | string {
    const ref = paragraphRefs.current[index]
    return ref ? ref.scrollHeight : 'auto'
  }

  return (
    <section
      className={classNames(styles.HowItWorksNc, {
        [styles.HowItWorksNc___referAFriend]: pageType === PageType.ReferAFriend,
      })}
    >
      <div className={styles.HowItWorksNc__container}>
        <h3 className={styles.HowItWorksNc__sectionHeading}>{sectionHeading}</h3>
        <div className={styles.HowItWorksNc__stepsWrapper}>
          {steps.map((step, i) => {
            return (
              <div
                key={step.id}
                className={styles.HowItWorksNc__stepContainer}
                onClick={() => handleStepClick(i)}
              >
                <NumberImage
                  ariaLabel={`step ${i + 1}`}
                  className={classNames(styles.HowItWorksNc__numberImage, {
                    [styles.HowItWorksNc__numberImage___active]: stepState.selectedStep === i,
                  })}
                  numeral={i + 1}
                  circleColor="#E2D4D4"
                  circleFillColor="#fff"
                  color={brandPurple}
                />
                <p className={styles.HowItWorksNc__subheading}>{step.subheading}</p>
                <p
                  ref={el => (paragraphRefs.current[i] = el)}
                  // Using styling here because we need to obtain maxHeight and minHeight
                  style={{
                    transition: 'all',
                    transitionDuration: '300ms',
                    height: stepState.selectedStep === i ? getParagraphHeight(i) : 0,
                    opacity: stepState.selectedStep === i ? 1 : 0,
                    transitionTimingFunction: 'ease-out',
                  }}
                  id={`${i}`}
                  className={classNames(styles.HowItWorksNc__paragraph)}
                >
                  {step.paragraph}
                </p>
              </div>
            )
          })}
        </div>

        {steps.map((step, i) => (
          <NcImage
            key={step.id}
            {...step.image}
            className={classNames(styles.HowItWorksNc__image, {
              [styles.HowItWorksNc__image___displayNoImage]: stepState.selectedStep !== i,
            })}
          />
        ))}
      </div>
    </section>
  )
}
